import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, MaskLine } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ROLES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAINS = [
  ["Incumbent Councillors", "First-mover advantage on C3 · Accountability Champion designation · 24/7 AI bridge to 5,000 constituents · independently verified performance data."],
  ["Aspiring Councillors", "Ward Vision Document co-authorship · Emerging Leaders certification · a credibility platform built on evidence before campaign season."],
  ["Former Councillors", "Advisory Board membership · Legacy Recognition programme · mentorship of Delhi's next governance generation."],
  ["Startups & Industry", "Featured solutions in the Good Governance Book · four partnership tiers · the largest ward-level governance market in India."],
  ["Government & Bureaucracy", "The most granular municipal performance dataset being built in India · a non-partisan platform to document and amplify achievement."],
  ["Academia & Civil Society", "A methodology-disclosed, replicable dataset · research partnerships · a direct channel from the ward to the national conversation."],
];

const inputCls =
  "h-12 rounded-sm border-white/15 bg-coal px-4 text-ivory placeholder:text-white/30 focus-visible:ring-gold";

const Register = () => {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", role: "", organisation: "", city: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role) {
      toast.error("Please select what you are registering as");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/register`, form);
      setDone(true);
      toast.success("Registration received. Our team will reach out shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Register"
        path="/register"
        description="Register with Smart Nagar Nigam — as an Incumbent, Former or Aspiring Councillor, StartUp, Business/Industry, Academia, Civil Society, Government Department or other stakeholder in Delhi's governance future."
      />
      <section className="relative overflow-hidden pt-28 md:pt-52" data-testid="register-hero">
        <div className="mx-auto max-w-[1600px] px-5 pb-16 md:px-10 md:pb-24">
          <MaskLine delay={0.1}>
            <span className="text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-gold md:text-sm">Registration</span>
          </MaskLine>
          <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-ivory">
            <MaskLine delay={0.25} className="text-[11vw] md:text-[6.5vw]">Take Your Seat</MaskLine>
            <MaskLine delay={0.4} className="text-[11vw] md:text-[6.5vw]">
              <span className="text-outline">in Delhi's Future.</span>
            </MaskLine>
          </h1>
          <MaskLine delay={0.55}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
              Whether you govern a ward, build for one, study one, or live in one — there is a
              defined place for you in this movement.
            </p>
          </MaskLine>
        </div>
      </section>

      <section className="border-t border-white/10" data-testid="register-section">
        <div className="mx-auto grid max-w-[1600px] gap-16 px-5 py-16 md:px-10 md:py-24 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            {done ? (
              <div className="border border-gold/40 bg-coal p-10 md:p-14" data-testid="register-success">
                <p className="text-xs uppercase tracking-[0.3em] text-gold">Registration Received</p>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ivory md:text-4xl">
                  Welcome to the movement, {form.full_name.split(" ")[0]}.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-white/65">
                  Your registration as <span className="text-gold">{form.role}</span> has been
                  recorded and a confirmation is on its way to your inbox. Our team will reach
                  out to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-white/10 bg-coal p-8 md:p-12" data-testid="register-form">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-xs uppercase tracking-[0.2em] text-white/50">Full Name *</Label>
                    <Input id="full_name" data-testid="register-name-input" required minLength={2} value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-white/50">Email *</Label>
                    <Input id="email" type="email" data-testid="register-email-input" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-white/50">Phone *</Label>
                    <Input id="phone" type="tel" data-testid="register-phone-input" required minLength={6} value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-white/50">Registering As *</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="register-role-select" className="h-12 rounded-sm border-white/15 bg-obsidian text-ivory focus:ring-gold">
                        <SelectValue placeholder="Select your category" />
                      </SelectTrigger>
                      <SelectContent className="border-white/15 bg-coal text-ivory">
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r} data-testid={`register-role-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="focus:bg-gold focus:text-obsidian">
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="organisation" className="text-xs uppercase tracking-[0.2em] text-white/50">Organisation</Label>
                    <Input id="organisation" data-testid="register-org-input" value={form.organisation} onChange={set("organisation")} placeholder="Organisation / Ward / Institution" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-xs uppercase tracking-[0.2em] text-white/50">City / Ward</Label>
                    <Input id="city" data-testid="register-city-input" value={form.city} onChange={set("city")} placeholder="e.g. Delhi — Ward 74" className={inputCls} />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="message" className="text-xs uppercase tracking-[0.2em] text-white/50">Message</Label>
                    <Textarea id="message" data-testid="register-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us how you would like to engage…" className="rounded-sm border-white/15 bg-coal px-4 text-ivory placeholder:text-white/30 focus-visible:ring-gold" />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="register-submit-button"
                  disabled={loading}
                  className="mt-8 inline-flex items-center gap-3 bg-gold px-10 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-obsidian transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {loading ? "Submitting…" : "Submit Registration"}
                </button>
              </form>
            )}
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="border border-white/10 p-8 md:p-10">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold">What Every Stakeholder Gains</p>
              <div className="mt-6 space-y-6">
                {GAINS.map(([k, v]) => (
                  <div key={k} className="border-b border-white/10 pb-6 last:border-b-0">
                    <h3 className="font-display text-base font-bold text-ivory">{k}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/55">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Register;
